import React, { useState } from 'react';
import { Search, Calendar, MapPin, Users, Target, TrendingUp, Package, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import BottomNav from '../components/common/BottomNav';
import NotificationBell from '../components/common/NotificationBell';
import { useNotification } from '../context/NotificationContext';

const CreateGroupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addNotification } = useNotification();
  
  // Check if coming from a similar group suggestion
  const similarTo = location.state?.similarTo;
  const suggestedCategory = location.state?.category || '';
  
  const [formData, setFormData] = useState({
    productInterest: similarTo?.title || '',
    category: suggestedCategory || '',
    description: '',
    minUnits: 25,
    maxUnits: 100,
    groupDuration: 7, // days
    pickupPreference: 'flexible',
    locationPreference: '',
    additionalNotes: ''
  });
  
  const [searchResults, setSearchResults] = useState([]);
  const [showingExamples, setShowingExamples] = useState(false);
  
  const categories = ['Groceries', 'Household', 'Personal Care', 'Baby', 'Electronics', 'Home & Garden', 'Health & Wellness', 'Arts & Crafts'];
  
  // Example products for inspiration
  const exampleProducts = {
    'Groceries': ['Organic Produce Bundle', 'Bulk Rice (25lbs)', 'Premium Coffee Beans', 'Local Honey Collection'],
    'Household': ['Eco-Friendly Cleaning Supplies', 'Bulk Paper Towels', 'Laundry Detergent (5 Gallons)', 'Trash Bags (500 count)'],
    'Personal Care': ['Natural Shampoo Set', 'Bulk Hand Soap', 'Organic Skincare Bundle', 'Bamboo Toothbrushes (50 pack)'],
    'Electronics': ['Wireless Earbuds', 'Smart Home Bundle', 'Phone Accessories Kit', 'Portable Chargers (10 pack)'],
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleProductSearch = (value) => {
    handleInputChange('productInterest', value);
    
    // In real app, this would search existing products/vendors
    if (value.length > 2) {
      // Simulate search results
      const mockResults = [
        { id: 1, name: 'Similar Product 1', vendor: 'Vendor A', avgPrice: '$25.99' },
        { id: 2, name: 'Similar Product 2', vendor: 'Vendor B', avgPrice: '$28.50' },
      ];
      setSearchResults(mockResults);
    } else {
      setSearchResults([]);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate minimum requirements
    if (!formData.productInterest || !formData.category) {
      addNotification({
        type: 'error',
        title: 'Missing Information',
        message: 'Please specify what product you want and select a category.',
        showToast: true
      });
      return;
    }
    
    // In real app, this would create the group and notify vendors
    addNotification({
      type: 'success',
      title: 'Group Interest Submitted!',
      message: 'Vendors will be notified and can submit offers. You\'ll be notified when vendors respond!',
      showToast: true
    });
    
    // Simulate group creation
    setTimeout(() => {
      navigate('/groups');
    }, 2000);
  };
  
  return (
    <div className="container mx-auto px-4 pb-20">
      {/* Header */}
      <div className="flex justify-between items-center mt-6 mb-4">
        <div>
          <h1 className="text-2xl font-bold">Start a Group</h1>
          <p className="text-gray-600 text-sm mt-1">Tell us what you want to buy together</p>
        </div>
        <NotificationBell />
      </div>
      
      {/* How It Works Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-900 mb-2">How Group Creation Works:</h3>
        <div className="space-y-2 text-sm text-blue-800">
          <div className="flex items-start">
            <span className="font-bold mr-2">1.</span>
            <span>Tell us what product you're interested in buying</span>
          </div>
          <div className="flex items-start">
            <span className="font-bold mr-2">2.</span>
            <span>Set your group preferences (size, timeline, location)</span>
          </div>
          <div className="flex items-start">
            <span className="font-bold mr-2">3.</span>
            <span>Vendors compete to offer the best prices</span>
          </div>
          <div className="flex items-start">
            <span className="font-bold mr-2">4.</span>
            <span>You become the group admin and help it succeed!</span>
          </div>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Product Interest Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <Package className="mr-2" size={20} />
            What do you want to buy?
          </h2>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Product or Service *
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
                type="text"
                className="w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:border-blue-500"
                placeholder="e.g., Organic vegetables, Cleaning supplies, Baby diapers..."
                value={formData.productInterest}
                onChange={(e) => handleProductSearch(e.target.value)}
                required
              />
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 border rounded-lg p-2 bg-gray-50">
                <p className="text-xs text-gray-600 mb-2">Similar active groups:</p>
                {searchResults.map(result => (
                  <div key={result.id} className="py-1 text-sm">
                    <span className="font-medium">{result.name}</span>
                    <span className="text-gray-600"> - {result.vendor} ({result.avgPrice})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Category *
            </label>
            <select
              className="w-full px-3 py-3 border rounded-lg focus:outline-none focus:border-blue-500"
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              required
            >
              <option value="">Select a category</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          
          {formData.category && exampleProducts[formData.category] && (
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setShowingExamples(!showingExamples)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {showingExamples ? 'Hide' : 'Show'} example products in {formData.category}
              </button>
              {showingExamples && (
                <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs font-medium text-blue-900 mb-2">Popular in this category:</p>
                  <div className="flex flex-wrap gap-2">
                    {exampleProducts[formData.category].map((example, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleInputChange('productInterest', example)}
                        className="text-xs bg-white px-3 py-1 rounded-full border border-blue-200 hover:bg-blue-100"
                      >
                        {example}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Additional Details
            </label>
            <textarea
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
              rows="3"
              placeholder="Any specific requirements? Brand preferences? Quality expectations?"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
            />
          </div>
        </div>
        
        {/* Group Settings Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <Users className="mr-2" size={20} />
            Group Settings
          </h2>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Minimum Units
              </label>
              <input
                type="number"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                value={formData.minUnits}
                onChange={(e) => handleInputChange('minUnits', parseInt(e.target.value))}
                min="10"
                max="1000"
              />
              <p className="text-xs text-gray-500 mt-1">Min needed for group</p>
            </div>
            
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Maximum Units
              </label>
              <input
                type="number"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                value={formData.maxUnits}
                onChange={(e) => handleInputChange('maxUnits', parseInt(e.target.value))}
                min="10"
                max="1000"
              />
              <p className="text-xs text-gray-500 mt-1">Max group can handle</p>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Group Duration
            </label>
            <div className="flex items-center space-x-4">
              {[3, 5, 7, 10, 14].map(days => (
                <button
                  key={days}
                  type="button"
                  onClick={() => handleInputChange('groupDuration', days)}
                  className={`px-4 py-2 rounded-lg border ${
                    formData.groupDuration === days
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300'
                  }`}
                >
                  {days}d
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">How long vendors can submit offers</p>
          </div>
        </div>
        
        {/* Location Preferences Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <MapPin className="mr-2" size={20} />
            Pickup Preferences
          </h2>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Pickup Flexibility
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="pickup"
                  value="flexible"
                  checked={formData.pickupPreference === 'flexible'}
                  onChange={(e) => handleInputChange('pickupPreference', e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm">Flexible - Any community pickup point</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="pickup"
                  value="specific"
                  checked={formData.pickupPreference === 'specific'}
                  onChange={(e) => handleInputChange('pickupPreference', e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm">Specific location preferred</span>
              </label>
            </div>
          </div>
          
          {formData.pickupPreference === 'specific' && (
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Preferred Location
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                placeholder="e.g., Near downtown, Midtown area..."
                value={formData.locationPreference}
                onChange={(e) => handleInputChange('locationPreference', e.target.value)}
              />
            </div>
          )}
        </div>
        
        {/* Admin Responsibilities */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-semibold text-yellow-900 mb-2 flex items-center">
            <Target className="mr-2" size={18} />
            As Group Admin, You'll:
          </h3>
          <ul className="space-y-1 text-sm text-yellow-800">
            <li>• Help promote the group to reach minimum orders</li>
            <li>• Communicate updates to group members</li>
            <li>• Earn bonus tokens for group success</li>
            <li>• Build your reputation as a community organizer</li>
          </ul>
        </div>
        
        {/* Creator Rewards Preview */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h3 className="font-semibold text-purple-900 mb-2 flex items-center">
            <TrendingUp className="mr-2" size={18} />
            Potential Creator Rewards:
          </h3>
          <div className="grid grid-cols-2 gap-2 text-sm text-purple-800">
            <div>• Creation bonus: 5 tokens</div>
            <div>• 10 members: +10 tokens</div>
            <div>• 25 members: +25 tokens</div>
            <div>• Group completes: +50 tokens</div>
          </div>
          <p className="text-xs text-purple-700 mt-2">
            Earn up to 100+ tokens by creating a successful group!
          </p>
        </div>
        
        {/* Submit Button */}
        <button
          type="submit"
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline transition-colors"
        >
          Submit Group Interest
        </button>
        
        <p className="text-xs text-gray-500 text-center">
          By creating a group, you agree to help it succeed and communicate with members
        </p>
      </form>
      
      <BottomNav />
    </div>
  );
};

export default CreateGroupPage;